import { useCallback, useEffect, useRef, useState } from 'react';

const SCROLL_BOUNDARY_TOLERANCE = 1;
const DRAG_THRESHOLD = 15;

export type NavigationTabsScrollState = {
	canScrollPrevious: boolean;
	canScrollNext: boolean;
	isScrollable: boolean;
};

export const useNavigationTabsScroll = () => {
	const viewportRef = useRef<HTMLDivElement>(null);
	const listRef = useRef<HTMLUListElement>(null);
	const dragStateRef = useRef<{ pointerId: number; startX: number; startScrollLeft: number }>();
	const suppressClickRef = useRef(false);
	const [scrollState, setScrollState] = useState<NavigationTabsScrollState>({
		canScrollPrevious: false,
		canScrollNext: false,
		isScrollable: false,
	});
	const clearDrag = useCallback((pointerId?: number) => {
		const viewport = viewportRef.current;
		const activePointerId = pointerId ?? dragStateRef.current?.pointerId;
		if (viewport && activePointerId !== undefined && viewport.hasPointerCapture(activePointerId))
			viewport.releasePointerCapture(activePointerId);
		dragStateRef.current = undefined;
	}, []);
	const clearAllDragState = useCallback(
		(pointerId?: number) => {
			clearDrag(pointerId);
			suppressClickRef.current = false;
		},
		[clearDrag],
	);

	const updateScrollState = useCallback(() => {
		const viewport = viewportRef.current;
		if (!viewport) return;

		const isScrollable = viewport.scrollWidth > viewport.clientWidth + SCROLL_BOUNDARY_TOLERANCE;
		setScrollState({
			isScrollable,
			canScrollPrevious: isScrollable && viewport.scrollLeft > SCROLL_BOUNDARY_TOLERANCE,
			canScrollNext:
				isScrollable &&
				viewport.scrollLeft <
					viewport.scrollWidth - viewport.clientWidth - SCROLL_BOUNDARY_TOLERANCE,
		});
	}, []);

	useEffect(() => {
		const viewport = viewportRef.current;
		if (!viewport) return;
		const resizeObserver =
			typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(updateScrollState);
		resizeObserver?.observe(viewport);
		if (listRef.current) resizeObserver?.observe(listRef.current);
		viewport.addEventListener('scroll', updateScrollState, { passive: true });
		updateScrollState();
		return () => {
			resizeObserver?.disconnect();
			viewport.removeEventListener('scroll', updateScrollState);
			clearAllDragState();
		};
	}, [clearAllDragState, updateScrollState]);

	const scrollBy = useCallback((distance: number) => {
		const viewport = viewportRef.current;
		if (!viewport) return;
		viewport.scrollBy({
			left: distance,
			behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
		});
	}, []);

	const onPointerDown = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			const viewport = viewportRef.current;
			clearAllDragState();
			if (
				!viewport ||
				!event.isPrimary ||
				event.button !== 0 ||
				viewport.scrollWidth <= viewport.clientWidth
			)
				return;
			dragStateRef.current = {
				pointerId: event.pointerId,
				startX: event.clientX,
				startScrollLeft: viewport.scrollLeft,
			};
			viewport.setPointerCapture(event.pointerId);
		},
		[clearAllDragState],
	);

	const onPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
		const viewport = viewportRef.current;
		const dragState = dragStateRef.current;
		if (!viewport || !dragState || dragState.pointerId !== event.pointerId) return;
		const distance = event.clientX - dragState.startX;
		if (Math.abs(distance) > DRAG_THRESHOLD) suppressClickRef.current = true;
		viewport.scrollLeft = dragState.startScrollLeft - distance;
	}, []);

	const onPointerEnd = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => clearDrag(event.pointerId),
		[clearDrag],
	);
	const onPointerCancel = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => clearAllDragState(event.pointerId),
		[clearAllDragState],
	);
	const onLostPointerCapture = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => clearDrag(event.pointerId),
		[clearDrag],
	);
	const onClickCapture = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
		if (!suppressClickRef.current) return;
		event.preventDefault();
		event.stopPropagation();
		suppressClickRef.current = false;
	}, []);

	return {
		viewportRef,
		listRef,
		scrollState,
		updateScrollState,
		scrollBy,
		onPointerDown,
		onPointerMove,
		onPointerEnd,
		onPointerCancel,
		onLostPointerCapture,
		onClickCapture,
	};
};
